import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, TemplateRef, ViewChild } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, trackByValue } from '@y42/angular/util';
import { SideDetailViewService } from '@y42/core/container-with-side-detail-view';
import { DataTestRunnerQuery, provideDataTestRunner } from '@y42/data-test';
import { isNullish } from '@y42/util/nullish';
import { wait } from '@y42/util/wait';
import { of } from 'rxjs';
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import { DataTestErrorDisplayInputParameter } from '../components/data-test-run-error-display/data-test-run-error-display.component';
import { DataTestBuilderFormService } from './data-test-builder-form.service';
import { DataTestBuilderSaveOutput, DataTestBuilderSidebarInput } from './data-test-builder.model';
import { DataTestBuilderService } from './data-test-builder.service';

@UntilDestroy()
@Component({
  selector: 'y42-data-test-builder',
  templateUrl: './data-test-builder.component.html',
  styleUrls: ['./data-test-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DataTestBuilderFormService, DataTestBuilderService, ...provideDataTestRunner()],
})
export class DataTestBuilderComponent implements AfterViewInit {
  constructor(
    private readonly formService: DataTestBuilderFormService,
    private readonly sideDetailViewService: SideDetailViewService<
      DataTestBuilderSaveOutput,
      DataTestBuilderSidebarInput
    >,
    private readonly dataTestBuilderService: DataTestBuilderService,
    private readonly dataTestRunnerQuery: DataTestRunnerQuery,
  ) {}

  @ViewChild('divScrollTo') divScrollTo!: ElementRef<HTMLDivElement>;
  @ViewChild('sideDetailViewFooter') sideDetailViewFooter!: TemplateRef<unknown>;

  readonly state = connectState(this, {
    testTypes: this.dataTestBuilderService.availableDataTests$,
    selectedTestType: this.formService.dataTestTypeFormControl.valueChanges.pipe(
      startWith(this.formService.dataTestTypeFormControl.value),
      distinctUntilChanged(),
    ),
    selectedTestTypeFC: of(this.formService.dataTestTypeFormControl),
    testRunResult: this.dataTestRunnerQuery.select((x): DataTestErrorDisplayInputParameter | null => {
      const { response, httpErrorOccurred } = x.runTest;
      if (isNullish(response) && !httpErrorOccurred) {
        return null;
      }
      return {
        response: response,
        httpErrorOccurred: httpErrorOccurred,
      };
    }),
    testRunResultLoading: this.dataTestRunnerQuery.select((x) => x.runTest.loading),
    runButtonDisabled: this.dataTestRunnerQuery.select((x) => x.runTest.loading),
  });
  readonly trackByFn = trackByValue;

  ngAfterViewInit() {
    this.sideDetailViewService.setFooter(this.sideDetailViewFooter);
  }

  async saveClick() {
    await this.dataTestBuilderService.saveDataTests();
  }

  async runTest() {
    await this.dataTestBuilderService.runTest();
    // wait for the content to be rendered
    await wait(100);
    this.divScrollTo.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  cancelClick() {
    this.sideDetailViewService.close();
  }
}
